import { z } from 'zod';
import { hasUnsafeReferenceCharacter, referenceTextSchema } from './primitives.js';
/**
 * The laboratory's printable business details and document wording
 * (decision D199): what a quotation prints about the laboratory that issues it.
 *
 * These are PRINTABLE BUSINESS DETAILS, not secrets. An IBAN, a SWIFT code and
 * an account number are printed on every quotation the laboratory hands out.
 * They are validated for SHAPE so a typing mistake is caught before a client
 * pays into the wrong account; nothing here checks that the account exists.
 *
 * NEVER BILINGUAL: identifiers (IBAN, account number, SWIFT, website, country)
 * are one value in every language. Only prose that is worded differently per
 * language — an introduction, conditions, payment-terms wording, a footer —
 * has an Arabic and an English form (CLAUDE.md 7.1, D187).
 */
/** An empty string and `null` both mean "none", and reach the server as null. */
const emptyToNull = (value) => (value === '' ? null : value);
/**
 * Document wording in both languages: optional per language, and longer than
 * a name — general conditions run to several paragraphs. No at-least-one rule:
 * "none" is a legitimate answer.
 */
export const documentWordingSchema = (max) => z.object({
    ar: z.string().trim().max(max).nullable().optional().transform(emptyToNull),
    en: z.string().trim().max(max).nullable().optional().transform(emptyToNull),
});
/** ISO 3166-1 alpha-2, e.g. `SA`. */
export const countryCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, 'A country is a two-letter ISO 3166 code');
/** A public website: http or https, nothing else a document could link to. */
export const websiteSchema = z
    .string()
    .trim()
    .max(200)
    .refine(isWebAddress, 'A website is a web address, e.g. https://example.com or www.example.com');
/**
 * A website as the laboratory writes it (D206): with or without `https://`,
 * stored as written. What is refused is what could never be a web address or
 * could be harmful where it is shown — spaces, control characters, and any
 * scheme other than http or https (`javascript:`, `data:`, `mailto:`). A port
 * (`example.com:8080`) is not a scheme.
 */
function isWebAddress(value) {
    if (/\s/.test(value) || hasUnsafeReferenceCharacter(value))
        return false;
    const scheme = /^([a-z][a-z0-9+.-]*):(?!\d)/i.exec(value);
    return !scheme || /^https?$/i.test(scheme[1] ?? '');
}
/**
 * True when `iban` passes the ISO 13616 check: two letters, two check digits,
 * up to 30 letters or digits, and the mod-97 remainder of 1. Spaces are
 * allowed for readability and ignored.
 */
/**
 * The laboratory's bank references (D206, replacing D199's shape checks): an
 * IBAN, a SWIFT / BIC code and a domestic account number, each stored and
 * printed EXACTLY as the laboratory wrote it. No checksum, no national length,
 * no case change — the laboratory is responsible for its own bank details.
 */
export const ibanSchema = referenceTextSchema(64);
export const swiftCodeSchema = referenceTextSchema(64);
export const bankAccountNumberSchema = referenceTextSchema(64);
/** Optional, nullable, and an empty string means none. */
export const optionalOf = (schema) => z.union([z.literal('').transform(() => null), schema]).nullable().optional();
//# sourceMappingURL=organisation.js.map